import { memo } from 'react';
import { useInView } from 'react-intersection-observer';
import { EnergyManagementChart } from '@/components/energy-management-chart.tsx';
import {
  useOverviewQuery,
  type DateRange,
} from '@/features/dashboard/hooks/queries/get-overview.ts';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/utils/utils.ts';
import { useMetricPermissions } from '@/hooks/use-metric-permissions.ts';

const EMPTY_ARRAY: never[] = [];

export const DashboardEnergyManagementChart = memo(function DashboardEnergyManagementChart({
  dateRange,
  viewContext,
  className,
}: {
  dateRange: DateRange;
  viewContext: string;
  className?: string;
}) {
  const hiddenMetrics = useMetricPermissions();

  const { ref, inView } = useInView({
    triggerOnce: false,
    threshold: 0,
    rootMargin: '20px',
  });

  const { data, isPending, isFetching } = useOverviewQuery({
    dateRange,
    viewContext,
    section: 'energyManagement',
    enabled: inView,
  });

  return (
    <div ref={ref} className={className}>
      {isPending ? (
        <Skeleton className="h-full min-h-[300px] w-full rounded-lg" />
      ) : (
        <EnergyManagementChart
          className={cn(isFetching && 'pointer-events-none animate-pulse opacity-60')}
          data={data?.energyManagement ?? EMPTY_ARRAY}
          rangeGranularity={data?.rangeGranularity ?? dateRange.rangeGranularity}
          currentViewContext={viewContext}
          hiddenMetrics={hiddenMetrics}
        />
      )}
    </div>
  );
});
